/**
 * Topbar dropdown "Wciel sie!" — admin/developer testuje rolami innych userow.
 *
 * Dziala przez httpOnly cookie 'impersonate-role' ustawiane przez POST /auth/impersonate.
 * Backend loadUser middleware czyta cookie i nadpisuje effective role.
 *
 * Po toggle: pelny reload zeby useAuth zaladowal /auth/me z nowa rola.
 */

import { useAuth } from '@/features/auth/useAuth';
import { apiFetch } from '@/lib/apiClient';
import { USER_ROLE_LABELS, type UserRole } from '@coreltb/shared/constants';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, UserCog } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const IMPERSONATABLE_ROLES: UserRole[] = ['admin', 'dyrektor_sprzedazy', 'marketing', 'handlowiec'];

export function ImpersonateMenu() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const setRoleMutation = useMutation({
    mutationFn: (role: UserRole | null) =>
      apiFetch<{ impersonating: string | null }>('/auth/impersonate', {
        method: 'POST',
        body: { role },
      }),
    onSuccess: () => {
      // Reload zeby cale UI dostalo nowa role z /auth/me
      qc.clear();
      window.location.assign('/');
    },
  });

  // Click outside → close
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Tylko admin/developer widzi opcje. Sprawdzamy actualRole — admin wcielony
  // jako handlowiec nadal ma actualRole=admin i moze sie odwcielic.
  if (!user || (user.actualRole !== 'developer' && user.actualRole !== 'admin')) {
    return null;
  }

  const isImpersonating = user.role !== user.actualRole;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={`inline-flex min-h-touch min-w-touch items-center justify-center gap-1.5 rounded-md border text-xs font-medium transition-colors md:h-9 md:min-h-0 md:min-w-0 md:px-2.5 ${
          isImpersonating
            ? 'border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 active:bg-amber-500/25'
            : 'border-border bg-surface-2 text-fg-muted hover:bg-surface-3 hover:text-fg active:bg-surface-3'
        }`}
        title={
          isImpersonating
            ? `Wcielono: ${USER_ROLE_LABELS[user.role]}. Klik, żeby zmienić.`
            : 'Wciel się w inną rolę (test)'
        }
      >
        <UserCog className="h-3.5 w-3.5" strokeWidth={2.5} />
        <span className="hidden xl:inline">
          {isImpersonating ? `Wcielono: ${USER_ROLE_LABELS[user.role]}` : 'Wciel się'}
        </span>
        <ChevronDown className="h-3 w-3" strokeWidth={2.5} />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-40 mt-2 w-64 overflow-hidden rounded-lg border border-border bg-surface shadow-lg">
          <div className="border-b border-border bg-bg/40 px-4 py-2.5">
            <h3 className="text-xs font-semibold text-fg-muted">Wciel się w rolę</h3>
            <p className="mt-1 text-xs text-fg-subtle">
              Tylko test. Faktyczna rola: {USER_ROLE_LABELS[user.actualRole]}
            </p>
          </div>
          <ul>
            {isImpersonating && (
              <li className="border-b border-border">
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    setRoleMutation.mutate(null);
                  }}
                  disabled={setRoleMutation.isPending}
                  className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm font-medium text-amber-700 dark:text-amber-300 hover:bg-surface-2"
                >
                  <span>Wróć do mojej roli ({USER_ROLE_LABELS[user.actualRole]})</span>
                </button>
              </li>
            )}
            {IMPERSONATABLE_ROLES.map((role) => {
              const isCurrent = user.role === role;
              return (
                <li key={role} className="border-b border-border last:border-b-0">
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      setRoleMutation.mutate(role);
                    }}
                    disabled={setRoleMutation.isPending || isCurrent}
                    className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-surface-2 disabled:opacity-50 ${
                      isCurrent ? 'bg-accent-subtle text-accent' : 'text-fg'
                    }`}
                  >
                    <span>{USER_ROLE_LABELS[role]}</span>
                    {isCurrent && <span className="text-xs">aktywne</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
